/**
 * `take <thing> from <place>` (D242) — the take, when the thing is in the
 * place named; the plain refusal when it is not.
 *
 * @remarks
 * The base vocabulary's `take` binds `from` since D242, so the place a
 * player names is no longer read as part of the thing's name ("You see no
 * band from tray here"). The place is a check, never a second act: when the
 * thing is in it — directly, or in something inside it (`take the shell from
 * the tray`, the shell in the allowance in the tray), or carried by the body
 * named (`take the key from the guard`, D240's pockets) — the clause is the
 * plain `take`, and every rule a take keeps (found, reached, portable, the
 * hands' capacity) is the take handler's, unchanged. When it is not there,
 * the `notInPlaceNamed` slot says so and nothing moves.
 *
 * "In it" is the thing's authored containment while the thing is still
 * where the author put it. A thing already in hand passes to the take, which
 * answers that it is already held — the truer sentence. A thing the player
 * set down somewhere is on that floor, in nothing. A ROOM named as the place
 * is where the thing is when it is the room the player stands in; the take
 * handler judges whether the thing is in it. The player's own hands (D63)
 * pass to the take as well: whatever is in them is already held. D244: a
 * STOCK named as both (`take a canister from the rack`, the rack answering
 * `canister`) is the take from it — the place a thing is taken from, and the
 * thing the words reach, are one fixed thing that gives.
 *
 * @packageDocumentation
 */

import type { EntityRef } from '@orpheus/parser-engine';

import { type ActorId, containerActorIdOf, containerItemIdOf, type ItemId, type RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandNarrowingOutcome } from './runtime-action.js';

import { requireItem } from '../bundle/bundle-lookups.js';
import { speakSlot } from '../defaults/default-responses.js';
import { actorIdForEntityRef, isBodyRef, itemIdForEntityRef, roomIdForEntityRef } from '../entities/entity-ref.js';
import { narrowed, rejected } from './narrow-parsed-command.js';

/**
 * Every holder above a thing, nearest first: the containers it sits in, and
 * the body that carries the outermost of them — as the author placed it.
 *
 * @param bundle - The loaded bundle
 * @param itemId - The thing
 * @returns The holders' ids, items and the one body alike
 */
const holdersOf = (bundle: EngineGameBundle, itemId: ItemId): readonly (ActorId | ItemId)[] => {
	const holders: (ActorId | ItemId)[] = [];
	let containedIn = requireItem(bundle, itemId).containedIn;

	// The loader refuses a containment cycle, so this walk ends at the outermost holder.
	while (containedIn !== undefined) {
		const containerActorId = containerActorIdOf(containedIn);
		const containerItemId = containerItemIdOf(containedIn);

		if (containerActorId !== undefined) {
			holders.push(containerActorId);
		}

		if (containerItemId === undefined) {
			break;
		}

		holders.push(containerItemId);
		containedIn = requireItem(bundle, containerItemId).containedIn;
	}

	return holders;
};

/**
 * Is the thing in the place named, as far as a take is concerned?
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state, when the caller has one
 * @param itemId - The thing to be taken
 * @param placeRef - The place the player named after `from`
 * @returns Whether the clause is the plain take
 */
const isInPlaceNamed = (bundle: EngineGameBundle, state: RuntimeState | undefined, itemId: ItemId, placeRef: EntityRef): boolean => {
	// In hand already, or the hands themselves: the take answers truly that it is held.
	if (isBodyRef(placeRef) || state?.world.inventoryItemIds.includes(itemId) === true) {
		return true;
	}

	const roomId = roomIdForEntityRef(placeRef);

	if (roomId !== undefined) {
		return state === undefined || state.progression.activeRoomId === roomId;
	}

	// Set down by the player: on that floor, in nothing.
	if (state?.world.droppedItemLocations[itemId] !== undefined) {
		return false;
	}

	const holderId: ActorId | ItemId = actorIdForEntityRef(placeRef) ?? itemIdForEntityRef(placeRef);

	// D244: `take a canister from the rack`, where the rack IS the canisters —
	// a stock named as both the thing and the place is the take from it.
	if (holderId === itemId && requireItem(bundle, itemId).stock !== undefined) {
		return true;
	}

	return holdersOf(bundle, itemId).includes(holderId);
};

/**
 * Narrow `take <thing> from <place>`: the plain take when the thing is there,
 * the refusal when it is not.
 *
 * @param bundle - The loaded bundle
 * @param state - The live runtime state, when the caller has one
 * @param itemId - The thing to be taken
 * @param placeRef - The place named after `from`
 * @returns The take, or the typed refusal
 */
export const narrowTakeFrom = (
	bundle: EngineGameBundle,
	state: RuntimeState | undefined,
	itemId: ItemId,
	placeRef: EntityRef,
): CommandNarrowingOutcome =>
	isInPlaceNamed(bundle, state, itemId, placeRef)
		? narrowed({ itemId, type: 'take' })
		: rejected(bundle, state, { itemId, type: 'not_in_place_named' }, [
			speakSlot(bundle, state, 'notInPlaceNamed', { itemName: requireItem(bundle, itemId).name }),
		]);
